import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { getAuthErrorMessage, sendPasswordReset } from '../services/authService';
import { logger } from '../utils/logger';
import '../../public/styles/main.css';

interface LoginPageProps {
    onLogin: (email: string, password: string) => Promise<void>;
    onSignup?: () => void;
}

export default function LoginPage({ onLogin, onSignup }: LoginPageProps) {
    const { strings } = useLanguage();
    const [ email, setEmail ] = useState('');
    const [ password, setPassword ] = useState('');
    const [ isLoading, setIsLoading ] = useState(false);
    const [ error, setError ] = useState('');
    const [ showResetModal, setShowResetModal ] = useState(false);
    const [ resetEmail, setResetEmail ] = useState('');
    const [ resetLoading, setResetLoading ] = useState(false);
    const [ resetError, setResetError ] = useState('');
    const [ resetSuccess, setResetSuccess ] = useState(false);

    // Email validation function
    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Check if reset email is valid
    const isResetEmailValid = resetEmail.trim() !== '' && isValidEmail(resetEmail);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            if (!email || !password) {
                setError(strings.LOGIN_FILL_FIELDS_ERROR);
                return;
            }

            // Call Firebase login through App.tsx
            await onLogin(email, password);
        } catch (err: any) {
            logger.error('Login error:', err);
            
            // Handle Firebase authentication errors
            if (err.code) {
                const errorMessage = getAuthErrorMessage(err.code, strings);
                setError(errorMessage);
            } else {
                setError(strings.LOGIN_ERROR);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPassword = () => {
        setShowResetModal(true);
        setResetEmail(email);
        setResetError('');
        setResetSuccess(false);
    };

    const handleResetSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setResetLoading(true);
        setResetError('');
        setResetSuccess(false);

        try {
            if (!resetEmail.trim()) {
                setResetError(strings.RESET_PASSWORD_EMAIL_REQUIRED);
                return;
            }

            if (!isValidEmail(resetEmail)) {
                setResetError(strings.INVALID_EMAIL_ERROR);
                return;
            }

            await sendPasswordReset(resetEmail);
            setResetSuccess(true);
            logger.log('Password reset email sent successfully');
        } catch (err: any) {
            logger.error('Password reset error:', err);
            
            if (err.code) {
                const errorMessage = getAuthErrorMessage(err.code, strings);
                setResetError(errorMessage);
            } else {
                setResetError(strings.RESET_PASSWORD_ERROR);
            }
        } finally {
            setResetLoading(false);
        }
    };

    const closeResetModal = () => {
        setShowResetModal(false);
        setResetEmail('');
        setResetError('');
        setResetSuccess(false);
    };

    return (
        <>
            <div className="container">
                <LanguageSwitcher />
                <form onSubmit={handleSubmit}>
                    <h3>{strings.APP_TITLE}</h3>
                    <label>
                        {strings.LOGIN_EMAIL_LABEL}
                        <input
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                            autoComplete="username"
                            placeholder={strings.LOGIN_EMAIL_PLACEHOLDER}
                        />
                    </label>

                    <label>
                        {strings.LOGIN_PASSWORD_LABEL}
                        <input
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                            autoComplete="current-password"
                            placeholder={strings.LOGIN_PASSWORD_PLACEHOLDER}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <button
                                type="button"
                                className="btn-underline"
                                onClick={handleForgotPassword}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.textDecoration = 'none';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.textDecoration = 'underline';
                                }}
                            >
                                {strings.FORGOT_PASSWORD_BUTTON}
                            </button>
                            <button
                                type="button"
                                className="btn-underline"
                                onClick={onSignup}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.textDecoration = 'none';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.textDecoration = 'underline';
                                }}
                            >
                                {strings.REGISTER_BUTTON}
                            </button>
                        </div>
                    </label>

                    <div>
                        {error && (
                            <div className="request-error">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading || !email || !password}
                            className={`btn-active ${isLoading || !email || !password ? 'btn-active-disabled' : ''}`}
                        >
                            {isLoading ? strings.LOGIN_LOADING : strings.LOGIN_BUTTON}
                        </button>
                    </div>
                </form>
            </div>

            {showResetModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h2>{strings.RESET_PASSWORD_TITLE}</h2>
                        </div>
                        <div className="modal-body">
                            {resetSuccess ? (
                                <div>
                                    <p>{strings.RESET_PASSWORD_SUCCESS}</p>
                                    <p>{strings.RESET_PASSWORD_INSTRUCTIONS}</p>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn-active"
                                            onClick={closeResetModal}
                                        >
                                            {strings.RESET_PASSWORD_CANCEL}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleResetSubmit}>
                                    <label>
                                        {strings.RESET_PASSWORD_EMAIL_LABEL}
                                        <input
                                            type="email"
                                            value={resetEmail}
                                            onChange={e => setResetEmail(e.target.value)}
                                            required
                                            autoComplete="email"
                                            placeholder={strings.RESET_PASSWORD_EMAIL_PLACEHOLDER}
                                            disabled={resetLoading}
                                        />
                                    </label>
                                    {resetError && (
                                        <div className="request-error">
                                            {resetError}
                                        </div>
                                    )}
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={closeResetModal}
                                            disabled={resetLoading}
                                        >
                                            {strings.RESET_PASSWORD_CANCEL}
                                        </button>
                                        <button
                                            type="submit"
                                            className={`btn-active ${resetLoading || !isResetEmailValid ? 'btn-active-disabled' : ''}`}
                                            disabled={resetLoading || !isResetEmailValid}
                                        >
                                            {resetLoading ? strings.RESET_PASSWORD_SENDING : strings.RESET_PASSWORD_SEND}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
